import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { motion } from "framer-motion";
import { database, storage } from "../../../../appWrite";
import { ID } from "appwrite";
import { fetchTestimonials } from "../../../../redux/slices/TestimonialSlice";
import { useAppDispatch } from "../../../../redux/hooks";

interface Testimonial {
  name: string;
  heading: string;
  review: string;
  occupation: string;
  image?: FileList;
}

const TestimonialModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Testimonial>();
  const [loading, setLoading] = useState(false);

  const onSubmit: SubmitHandler<Testimonial> = async (data) => {
    setLoading(true);
    try {
      let imageUrl = "";

      // Upload Image if provided
      if (data.image && data.image.length > 0) {
        const imageFile = data.image[0];
        const imageUpload = await storage.createFile(
          "67d13a3c002fd583a14b",
          ID.unique(),
          imageFile
        );
        imageUrl = storage
          .getFilePreview("67d13a3c002fd583a14b", imageUpload.$id)
          .toString();
      }

      // Save Testimonial
      await database.createDocument(
        "67ce8a4a0032e13c6368",
        "67d7eb5a0013791e9da4",
        ID.unique(),
        {
          name: data.name,
          heading: data.heading,
          review: data.review,
          image: imageUrl,
          occupation: data.occupation,
        }
      );

      dispatch(fetchTestimonials());
      reset();
      onClose();
    } catch (error) {
      console.error("Error adding testimonial:", error);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const input =
    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary";
  const btnPrimary =
    "px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90";
  const btnSecondary =
    "px-4 py-2 bg-gray-200 text-black rounded-md hover:bg-gray-300";

  return (
    <motion.div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-3 md:mx-0"
      >
        <h2 className="text-xl font-bold mb-4">Add Testimonial</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input
            {...register("name", { required: true })}
            placeholder="Name"
            className={`${input}`}
          />
          {errors.name && (
            <p className="text-red-500 text-sm">Name is required</p>
          )}

          <input
            {...register("occupation", { required: true })}
            placeholder="Occupation"
            className={`${input}`}
          />
          {errors.occupation && (
            <p className="text-red-500 text-sm">Occupation is required</p>
          )}

          <input
            {...register("heading", { required: true })}
            placeholder="Heading"
            className={`${input}`}
          />
          {errors.heading && (
            <p className="text-red-500 text-sm">Heading is required</p>
          )}

          <textarea
            {...register("review", { required: true })}
            placeholder="Review"
            className={`${input}`}
          ></textarea>
          {errors.review && (
            <p className="text-red-500 text-sm">Review is required</p>
          )}

          <input type="file" {...register("image")} className={`${input}`} />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`${btnSecondary}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${btnPrimary}`}
              disabled={loading}
            >
              {loading ? "Saving..." : "Submit"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TestimonialModal;
